import { useRouter } from 'next/router'
import * as R from 'ramda'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Col, Row } from '../../../components/Layout/FlexGrid'
import MaxWidth from '../../../components/Layout/MaxWidth'
import NextSeo from '../../../components/NextSeo'
import Pagination from '../../../components/Pagination'
import { OrganizationSkeleton } from '../../../components/Skeleton/index'
import SectionTitle from '../../../components/Text/SectionTitle'
import OrganizationInfo from '../../../containers/OrganizationDetail/OrganizationInfo'
import ProjectCard from '../../../containers/ProjectCard'
import { useTracking } from '../../../hooks'
import { withTranslation } from '../../../i18n'
import EmptyIcon from '../../../public/static/images/img-search-empty.svg'
import { generateShareMsg, setShowErrorPage } from '../../../redux/app/actions'
import {
  getDonationStatsRequest,
  getOrgPageRequest
} from '../../../redux/project/actions'
import { orgSelector } from '../../../redux/project/selectors'
import { getColor, getFontSize } from '../../../utils/cssUtils'
import {
  organizationLoadMore,
  organizationView
} from '../../../utils/dataTracker'
import mq from '../../../utils/mediaQuery'
import { isNilOrEmpty } from '../../../utils/ramdaUtils'
import { slugify } from '../../../utils/urlUtils'

const EmptyPage = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  text-align: center;
  color: ${getColor('grey')};
`
const EmptyTitle = styled.span`
  margin-top: 32px;
  font-size: ${getFontSize('headline')};
  line-height: 1.23;
`
const EmptyContent = styled.p`
  margin-top: 8px;
  font-size: ${getFontSize('title')};
  line-height: 1.44;
  margin-bottom: 118px;
`
const SectionTitleWrapper = styled(MaxWidth)`
  border-top: 1px solid ${getColor('border')};
  padding: 24px 0px 16px 0px;
  ${mq.mobile} {
    padding: 16px 0px 8px 0px;
  }
`
const Wrapper = styled.div`
  margin-bottom: 40px;
  margin-top: 24px;
  ${mq.mobile} {
    margin-top: 16px;
  }
  width: 100%;
`
const PaginationWrapper = styled(MaxWidth)`
  display: flex;
  justify-content: flex-end;
  ${mq.tablet} {
    margin-bottom: 32px;
  }
  ${mq.mobile} {
    justify-content: center;
    margin-bottom: 16px;
  }
`

const sortings = ['newest', 'hottest', 'targetReached']

const Organization = ({ t, organizationNo, organizationName }) => {
  const dispatch = useDispatch()

  const track = useTracking()

  const router = useRouter()

  const isInitialRender = useSelector(R.path(['appState', 'isInitialRender']))

  const isAppview = useSelector(R.path(['appState', 'isAppview']))

  const latestProjectsNos = useSelector(
    R.path(['projectState', 'result', 'projects'])
  )

  const page = useSelector(R.path(['projectState', 'page']))

  const limit = useSelector(R.path(['projectState', 'limit']))

  const total = useSelector(R.path(['projectState', 'total']))

  const fetching = useSelector(
    R.path(['projectState', 'fetching', 'getOrgPage'])
  )

  const organizationData = useSelector(
    state => orgSelector(state, organizationNo),
    R.equals
  )

  organizationName = organizationData?.name ?? organizationName ?? ''

  const handleChangePage = async page => {
    router.push(
      `/org/${organizationNo}/${slugify(organizationName)}?page=${page}`
    )

    track(organizationLoadMore(organizationNo, organizationName, page))
  }

  useEffect(() => {
    if (isInitialRender && !isNilOrEmpty(latestProjectsNos)) {
      dispatch(getDonationStatsRequest(latestProjectsNos))
    }

    dispatch(
      generateShareMsg(
        t('shareMsg.organization', { orgName: organizationName })
      )
    )

    track(organizationView(organizationNo, organizationName))
  }, [])

  if (R.isNil(organizationData)) return null

  return (
    <Wrapper>
      {fetching ? (
        <OrganizationSkeleton />
      ) : R.isNil(organizationData) ? null : (
        <>
          <NextSeo
            title={`${t('seo.orgListingPage.ogTitle', {
              orgName: organizationName
            })}`}
            description={`${t('seo.orgListingPage.ogDescription', {
              orgName: organizationName
            })}`}
          />
          <OrganizationInfo organizationNo={organizationNo} />
          <SectionTitleWrapper>
            <SectionTitle>{t('organization.projects')}</SectionTitle>
          </SectionTitleWrapper>
          <MaxWidth>
            {R.isEmpty(latestProjectsNos) ? (
              <EmptyPage>
                <EmptyIcon />
                <EmptyTitle>{t('organization.emptyProjects')}</EmptyTitle>
                <EmptyContent>
                  {t('organization.goForOtherProjects')}
                </EmptyContent>
              </EmptyPage>
            ) : (
              <Row
                gutter={[24, 24]}
                css={`
                  margin-bottom: 24px;
                `}
              >
                {R.addIndex(R.map)(
                  (projectNo, index) => (
                    <Col xs={24} md={12} xl={8} key={projectNo}>
                      <ProjectCard
                        projectNo={projectNo}
                        page={page}
                        index={index}
                        projectType='normal'
                      />
                    </Col>
                  ),
                  latestProjectsNos
                )}
              </Row>
            )}
          </MaxWidth>
        </>
      )}
      <PaginationWrapper>
        <Pagination
          defaultCurrent={1}
          current={page}
          pageSize={limit}
          total={total}
          onChange={handleChangePage}
          showLess={isAppview}
        />
      </PaginationWrapper>
    </Wrapper>
  )
}

Organization.getInitialProps = async ctx => {
  const { res, query, store } = ctx

  const {
    organizationNo,
    organizationName,
    page = 1,
    sort = sortings[0]
  } = query

  const limit = 12

  if (!R.isNil(organizationNo)) {
    if (R.test(/ORG\d{5}$/, organizationNo)) {
      store.dispatch(getOrgPageRequest(page, limit, sort, organizationNo, ctx))

      return {
        sort,
        organizationNo,
        organizationName
      }
    } else {
      // 404
      res.status(404)
      store.dispatch(setShowErrorPage(true, 404))
    }
  }
}

export default withTranslation('common')(Organization)
